import { clickNav } from "../lib/click-nav.js";
import { draggable } from "../lib/draggable.js";

/*
 * Horizontal scroller with a custom scrollbar (Figma: "Group 195").
 *
 * Every file it needs, and how to reuse it: docs/scroller.md
 *
 * The panels scroll natively — trackpad swipe, touch, shift+wheel — so
 * momentum, snapping and accessibility all come from the browser. The native
 * bar is hidden in scroller.css and this draws the designed one in its place,
 * keeping the two in sync in both directions:
 *
 *   track scrolls   → thumb moves      (syncThumb)
 *   thumb dragged   → track scrolls    (bindThumbDrag)
 *   bar clicked     → track glides     (bindBarClick)
 *   track clicked   → track nudges     (bindTrackClick)
 *
 * Clicking the panels themselves works like the slick carousels: an arrow
 * cursor shows which way a click will go, left half back, right half on.
 *
 * A track with .scroller-fade also fades its panels in and out at the edges.
 * That is pure CSS where the browser supports scroll-driven animations; where
 * it does not (Firefox, for now) bindFadeFallback does the same job here.
 *
 * Expected markup — worked example in content/inc.scroller.php (several
 * per page is fine; each is independent):
 *
 *   .scroller-content
 *     .scroller-track      the overflow-x: auto row of panels
 *       …panels
 *     .scroller-bar        the line and end ticks
 *       .scroller-thumb    the dark block
 *
 * No-ops unless that markup is present. Not to be confused with `.scroller`,
 * which is the unrelated scroll-to-anchor link class in app.js.
 */

export default function initScroller() {
  document.querySelectorAll(".scroller-content").forEach(setup);
}

/** Wire one .scroller-content up. Bails out quietly if a part is missing. */
function setup(root) {
  const track = root.querySelector(".scroller-track");
  const bar = root.querySelector(".scroller-bar");
  const thumb = root.querySelector(".scroller-thumb");

  if (!track || !bar || !thumb) {
    return;
  }

  const parts = { track, bar, thumb, dragging: false };

  track.addEventListener("scroll", () => syncThumb(parts), { passive: true });
  bindThumbDrag(parts);
  bindBarClick(parts);
  bindTrackClick(parts);
  observeSize(parts);

  if (needsFadeFallback(track)) {
    bindFadeFallback(track);
  }

  syncThumb(parts);
}

/* ------------------------------------------------------------------------
 * Geometry
 * --------------------------------------------------------------------- */

/** How far the track can scroll, in px. 0 when everything fits. */
function maxScroll({ track }) {
  return Math.max(track.scrollWidth - track.clientWidth, 0);
}

/**
 * Whether the track has anywhere left to go that way. The 1px of slack
 * absorbs the fractional scrollLeft some browsers report at the very end.
 */
function canScroll(parts, direction) {
  const left = parts.track.scrollLeft;

  return direction === "prev" ? left > 1 : left < maxScroll(parts) - 1;
}

/** How far the thumb can travel along the bar, in px. */
function maxThumbLeft({ bar, thumb }) {
  return Math.max(bar.clientWidth - thumb.offsetWidth, 0);
}

/* ------------------------------------------------------------------------
 * Track → thumb
 * --------------------------------------------------------------------- */

/**
 * Place the thumb to match the track's scroll position, and hide the bar
 * when there is nothing to scroll (e.g. panels that all fit on a wide
 * screen). Skipped mid-drag: the drag already positions the thumb, and
 * recomputing it from a rounded scrollLeft would make it jitter.
 */
function syncThumb(parts) {
  const max = maxScroll(parts);

  parts.bar.hidden = max === 0;

  if (parts.dragging) {
    return;
  }

  const progress = max === 0 ? 0 : parts.track.scrollLeft / max;
  parts.thumb.style.left = `${progress * maxThumbLeft(parts)}px`;
}

/* ------------------------------------------------------------------------
 * Thumb → track
 * --------------------------------------------------------------------- */

/**
 * Dragging the thumb scrolls the track proportionally. draggable() handles
 * the pointer capture; the containment pins it to the bar's length and to
 * its own row, so it only ever moves sideways.
 */
function bindThumbDrag(parts) {
  const { track, thumb } = parts;
  const top = thumb.offsetTop;

  thumb.addEventListener("pointerdown", () => {
    parts.dragging = true;
  });

  draggable(thumb, {
    containment: () => ({ left: 0, right: maxThumbLeft(parts), top, bottom: top }),
    onDrag: ({ left }) => {
      const travel = maxThumbLeft(parts);
      track.scrollLeft = travel === 0 ? 0 : (left / travel) * maxScroll(parts);
    },
    onStop: () => {
      parts.dragging = false;
      syncThumb(parts);
    },
  });
}

/* ------------------------------------------------------------------------
 * Bar click → track
 * --------------------------------------------------------------------- */

/**
 * Clicking the bar anywhere off the thumb glides the track so the thumb
 * centres on the click point. The scroll events that follow move the thumb,
 * so it animates along with the panels for free.
 */
function bindBarClick(parts) {
  const { track, bar, thumb } = parts;

  bar.addEventListener("pointerdown", (event) => {
    if (event.target === thumb) {
      return;
    }

    const travel = maxThumbLeft(parts);
    const clickX = event.clientX - bar.getBoundingClientRect().left;
    const left = Math.min(Math.max(clickX - thumb.offsetWidth / 2, 0), travel);

    track.scrollTo({
      left: travel === 0 ? 0 : (left / travel) * maxScroll(parts),
      behavior: "smooth",
    });
  });
}

/* ------------------------------------------------------------------------
 * Track click → track
 * --------------------------------------------------------------------- */

/*
 * How far one click on the panels scrolls, as a fraction of the track's
 * visible width. 0.5 is about one panel on desktop, where panels are half
 * the track. Raise it for bigger jumps, lower it for a gentler nudge.
 */
const CLICK_STEP = 0.5;

/*
 * Mouse and trackpad only. On touch the panels are swiped, and a tap on
 * them is usually someone reading, not asking to move.
 */
const HOVER_POINTER = window.matchMedia("(hover: hover) and (pointer: fine)");

/**
 * Clicking the panels nudges the track one step left or right, with the
 * arrow cursor from lib/click-nav.js. At either end the arrow for the closed
 * direction drops back to the normal cursor, so it never promises a move
 * that cannot happen. The thumb follows through the scroll events, as with
 * bindBarClick.
 *
 * Checked at bind time: a device that gains a mouse later (a tablet with a
 * keyboard case, say) picks this up on the next page load.
 */
function bindTrackClick(parts) {
  if (!HOVER_POINTER.matches) {
    return;
  }

  const { track } = parts;

  /** Scroll by one step; -1 is back, 1 is on. */
  const nudge = (sign) =>
    track.scrollBy({ left: sign * track.clientWidth * CLICK_STEP, behavior: "smooth" });

  clickNav(track, {
    onPrev: () => nudge(-1),
    onNext: () => nudge(1),
    canGo: (direction) => canScroll(parts, direction),
  });
}

/* ------------------------------------------------------------------------
 * Resizing
 * --------------------------------------------------------------------- */

/**
 * Re-sync whenever the track or any panel changes size: window resizes,
 * breakpoint changes, images loading in, or panels switching from the
 * placeholder h-[100vh] to auto height. The panels are observed as well as
 * the track because a panel growing changes scrollWidth without resizing
 * the track itself.
 */
function observeSize(parts) {
  const observer = new ResizeObserver(() => syncThumb(parts));

  observer.observe(parts.track);
  observer.observe(parts.bar);
  Array.from(parts.track.children).forEach((panel) => observer.observe(panel));
}

/* ------------------------------------------------------------------------
 * Fade fallback (.scroller-fade without CSS scroll-driven animations)
 * --------------------------------------------------------------------- */

/*
 * Keep in step with @keyframes scroller-fade in scroller.css: the opacity a
 * panel sits at when it is fully out of view.
 */
const FADE_MIN_OPACITY = 0.2;

/**
 * Only when the track asks for the fade, the browser cannot do it in CSS,
 * and the visitor has not asked for reduced motion — the same conditions
 * under which the CSS version would have run.
 */
function needsFadeFallback(track) {
  return (
    track.classList.contains("scroller-fade") &&
    !CSS.supports("animation-timeline", "view()") &&
    !window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

/**
 * Set each panel's opacity from how much of it is inside the track: 0.2
 * when it is out of view, 1 once it is fully in. That is what the CSS
 * `view(inline)` timeline works out, so the two look the same.
 *
 * A panel wider than the track can never be fully inside it, so its ratio
 * is measured against the track's width instead.
 *
 * Updates are batched to one per frame; scroll fires far more often.
 */
function bindFadeFallback(track) {
  const panels = Array.from(track.children);
  let queued = false;

  const update = () => {
    queued = false;

    const view = track.getBoundingClientRect();

    panels.forEach((panel) => {
      const rect = panel.getBoundingClientRect();
      const visible = Math.min(rect.right, view.right) - Math.max(rect.left, view.left);
      const ratio = Math.min(Math.max(visible / Math.min(rect.width, view.width), 0), 1);

      panel.style.opacity = FADE_MIN_OPACITY + (1 - FADE_MIN_OPACITY) * ratio;
    });
  };

  const queue = () => {
    if (!queued) {
      queued = true;
      requestAnimationFrame(update);
    }
  };

  track.addEventListener("scroll", queue, { passive: true });
  new ResizeObserver(queue).observe(track);

  update();
}
